import type { ComponentTemplate } from "@editor/types/component-template";
import type {
  ComponentTemplateCategoryId,
  ComponentTemplateIndustryId,
} from "schemas/componentTemplates";
import type { ComponentTemplateScope } from "schemas/generated/componentTemplates";

import React, { useState } from "react";

import {
  selectDraftElementId,
  selectProjectId,
} from "@editor/reducers/core-reducer";
import { useEditorSelector } from "@editor/store";
import { routes } from "@editor/utils/router";

import Button from "@replo/design-system/components/button/Button";
import { ArrowLeft } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

import SearchTemplates from "./SearchTemplates";
import TemplateFilters from "./TemplateFilters";
import TemplatePage from "./TemplatePage";

export type TemplateDevices = "mobile" | "desktop";

const ChooseTemplateOverlay = () => {
  const navigate = useNavigate();
  const draftElementId = useEditorSelector(selectDraftElementId);
  const projectId = useEditorSelector(selectProjectId);

  const [selectedIndustryIds, setSelectedIndustryIds] = useState<
    ComponentTemplateIndustryId[]
  >([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<
    ComponentTemplateCategoryId[]
  >([]);
  const [previewPlatform, setPreviewPlatform] =
    React.useState<TemplateDevices>("desktop");
  const [subpageTemplate, setSubpageTemplate] =
    useState<ComponentTemplate | null>(null);
  const [templateScope, setTemplateScope] =
    useState<Omit<ComponentTemplateScope, "left-bar" | "unlisted">>("global");

  const handleBack = () => {
    if (draftElementId) {
      navigate(
        generatePath(routes.editor.element, {
          projectId: projectId,
          elementId: draftElementId,
        }),
      );
    } else {
      navigate(generatePath(routes.home.root));
    }
  };

  return (
    <div className="bg-neutral-soft flex h-full w-full items-center justify-center">
      {subpageTemplate ? (
        <TemplatePage
          template={subpageTemplate}
          setSubpageTemplate={setSubpageTemplate}
          previewPlatform={previewPlatform}
          setPreviewPlatform={setPreviewPlatform}
        />
      ) : (
        <div className="border-0.5 border-secondary ml-19 mr-2 mt-2 flex h-[calc(100vh-16px)] w-full flex-col items-center gap-12 overflow-hidden rounded-xl bg-white pl-24 pr-24 pt-12">
          <div className="relative flex w-full items-center justify-center">
            <Button
              variant="secondary"
              size="base"
              layoutClassName="absolute left-0"
              onClick={handleBack}
              startEnhancer={<ArrowLeft size={16} />}
            >
              Back
            </Button>
            <div className="text-3xl font-semibold">
              What would you like to build today?
            </div>
          </div>

          <div className="flex w-full gap-6">
            <TemplateFilters
              selectedIndustryIds={selectedIndustryIds}
              setSelectedIndustryIds={setSelectedIndustryIds}
              selectedCategoryIds={selectedCategoryIds}
              setSelectedCategoryIds={setSelectedCategoryIds}
              templateScope={templateScope}
              setTemplateScope={setTemplateScope}
            />
            <SearchTemplates
              selectedIndustryIds={selectedIndustryIds}
              selectedCategoryIds={selectedCategoryIds}
              setSubpageTemplate={setSubpageTemplate}
              previewPlatform={previewPlatform}
              setPreviewPlatform={setPreviewPlatform}
              templateScope={templateScope}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChooseTemplateOverlay;
